import { endpoints } from '@/api/endpoints/endpoints';
import ActionPopover from '@/components/action/action-popover';
import AsyncDataTable from '@/components/core/async-data-table/async-data-table';
import FilterHeader from '@/components/core/async-data-table/filter-header';
import { DatableColumnType } from '@/components/core/async-data-table/type';
import BlockCustomerDialog from '@/components/customers/BlockCustomerDialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { routes } from '@/routes/route-constants';
import { useDeleteCustomer } from '@/services/customer-service';
import { Customer } from '@/types/customer-type';
import MenuItem from '@mui/material/MenuItem';
import { Ban, History, Phone, Plus, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';

// Responsive Hook
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 768);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);
  return isMobile;
};

const CustomerAsyncTable = () => {
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();
  const deleteCustomer = useDeleteCustomer();
  const [refreshPage, setRefreshPage] = useState(false);
  const [selectedCustomerForBlock, setSelectedCustomerForBlock] = useState<Customer | null>(null);
  const [blockDialogTab, setBlockDialogTab] = useState<'action' | 'history'>('action');
  const { toast } = useToast();

  const handleDelete = async (customerId: string) => {
    try {
      await deleteCustomer.executeAsync(customerId);
      toast({ title: 'Success', description: 'Customer deleted successfully' });
      setRefreshPage(!refreshPage);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete customer', variant: 'destructive' });
    }
  };

  const cols: DatableColumnType[] = useMemo(
    () => [
      {
        name: 'name',
        id: 'name',
        label: 'Customer',
        searchable: true,
        orderable: true,
        datatype: 'string' as const,
        Render: ({ row }: { row: any }) => {
          const name = row?.name || 'Unknown';
          return (
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9 border shadow-sm">
                <AvatarImage src={row.profileImageUrl} alt={row.name} />
                <AvatarFallback className="bg-indigo-100 text-indigo-700 text-[10px] font-bold">
                  {name.substring(0, 2).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col min-w-0">
                <div className="flex items-center gap-1.5">
                  <h4 className="text-sm font-semibold text-gray-900 truncate">{row.name}</h4>
                  {row.blocked && (
                    <Badge
                      variant="destructive"
                      className="h-4 px-1.5 text-[10px] gap-0.5 shrink-0"
                      title={row.blockReason ? `Blocked: ${row.blockReason}` : 'Blocked customer'}
                    >
                      <Ban className="w-2.5 h-2.5" />
                      Blocked
                    </Badge>
                  )}
                </div>
                {row.blocked && row.blockReason && (
                  <span className="text-[11px] text-red-600 truncate max-w-[220px]">
                    Reason: {row.blockReason}
                  </span>
                )}
                {isMobile && row.saleChannel && (
                  <span className="text-[10px] text-muted-foreground uppercase tracking-tight">
                    {row.saleChannel}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      // Hide standalone phone on mobile, merge into Spent column or name
      ...(!isMobile
        ? [
            {
              name: 'phone',
              id: 'phone',
              label: 'Phone',
              searchable: true,
              datatype: 'string' as const,
            },
            {
              name: 'address',
              id: 'address',
              label: 'Address',
              datatype: 'string' as const,
            },
            {
              name: 'saleChannel',
              id: 'saleChannel',
              label: 'Channel',
              searchable: true,
              datatype: 'string' as const,
            },
          ]
        : []),
      {
        name: 'totalSpend',
        id: 'total_spend',
        label: isMobile ? 'Overview' : 'Spent',
        orderable: true,
        datatype: 'custom' as const,
        Render: ({ row }: { row: any }) => {
          const amount = row.totalSpend ?? 0;
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(amount);

          return (
            <div className={`${isMobile ? 'text-right' : 'text-left'} flex flex-col`}>
              <span className="text-sm font-bold text-green-700">{formatted}</span>
              {isMobile && row.phone && (
                <span className="text-[10px] text-gray-500 flex items-center justify-end gap-1">
                  <Phone size={10} /> {row.phone}
                </span>
              )}
            </div>
          );
        },
      },
      // Hide Creation Date on mobile to prioritize financial info
      ...(!isMobile
        ? [
            {
              name: 'createdAt',
              id: 'createdAt',
              label: 'Joined',
              Render: ({ row }: { row: any }) => {
                if (!row.createdAt) return '-';
                return (
                  <span className="text-xs">{new Date(row.createdAt).toLocaleDateString()}</span>
                );
              },
            },
          ]
        : []),
      {
        name: 'action',
        id: 'action',
        label: '',
        Render: ({ row }: { row: any }) => (
          <ActionPopover
            onEdit={() => setLocation(routes.customer.edit(row.id))}
            onDelete={() => handleDelete(row.id)}
          >
            <MenuItem
              onClickCapture={(e) => {
                e.stopPropagation();
                setBlockDialogTab('action');
                setSelectedCustomerForBlock(row);
              }}
              sx={{ color: row.blocked ? 'success.main' : 'warning.dark', gap: 1 }}
            >
              {row.blocked ? (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  Unblock Customer
                </>
              ) : (
                <>
                  <Ban className="w-4 h-4" />
                  Block Customer
                </>
              )}
            </MenuItem>
            <MenuItem
              onClickCapture={(e) => {
                e.stopPropagation();
                setBlockDialogTab('history');
                setSelectedCustomerForBlock(row);
              }}
              sx={{ gap: 1 }}
            >
              <History className="w-4 h-4" />
              Block History
            </MenuItem>
          </ActionPopover>
        ),
      },
    ],
    [isMobile, setLocation, refreshPage],
  );

  return (
    <div className="customer-table-mobile-wrapper">
      <AsyncDataTable
        dataUrl={endpoints.customer.list}
        columns={cols}
        autoIndex={!isMobile} // Hide index on mobile to save space
        FilterComponent={FilterHeader}
        refreshPage={refreshPage}
        showPageHeader={false}
        orderByDef="updated_at"
        headerAction={() => setLocation('/customers/new')}
        onClickRow={(row) => setLocation(routes.customer.detail(row.id))}
        headerActionText="Add Customer"
        headerActionIcon={<Plus size={18} />}
      />

      <BlockCustomerDialog
        customer={selectedCustomerForBlock}
        open={Boolean(selectedCustomerForBlock)}
        onOpenChange={(open) => {
          if (!open) setSelectedCustomerForBlock(null);
        }}
        defaultTab={blockDialogTab}
        onSuccess={() => {
          setRefreshPage((prev) => !prev);
        }}
      />
    </div>
  );
};

export default CustomerAsyncTable;

